/**
 * Standard soccar arena data: boost pads, spawns, goals and wall geometry.
 *
 * Pure data — no behaviour. Everything here is in unreal units (uu) in OUR
 * coordinate frame (+X toward the orange-side right wall, +Y toward the orange
 * goal, +Z up), which is the RLBot/RocketSim/RLUtilities numeric frame. See
 * docs/PHYSICS_REFERENCE.md §6 for the handedness caveat before using any of
 * these numbers to build a chiral quantity (cross products, torque signs).
 *
 * Same sourcing rule as constants.ts: every value names a source and carries a
 * confidence tag. Source abbreviations are defined at the top of
 * src/physics/constants.ts. Full table in docs/CONSTANTS_SOURCES.md.
 */

import type { Vec3 } from './math';

// ---------------------------------------------------------------------------
// Arena planes and wall geometry
// ---------------------------------------------------------------------------

/**
 * The analytic arena: 4 planes plus the 45-degree corner planes.
 *
 * Fidelity note: neither simulator uses only planes. RocketSim adds exactly
 * FOUR btStaticPlaneShapes for soccar (floor, ceiling, both X walls) and gets
 * the back walls and corners entirely from triangle meshes, because the goal
 * mouths are open. RLUtilities declares 10 walls but marks the Y walls and the
 * corner walls `collidable = false` and likewise relies on its meshes. So the
 * plane set below is an APPROXIMATION of the corners and back walls, exact for
 * floor, ceiling and side walls.
 * [confirmed: RS Arena.cpp fnAddPlane calls; RU field.cc initialize_soccar]
 */
export const ARENA_PLANES = {
  /** [confirmed: RS `ARENA_EXTENT_X`, RU walls[2..3], BW, RG] */
  sideWallX: 4096,
  /** [confirmed: RS `ARENA_EXTENT_Y`, RU walls[4..5], BW, RG] */
  backWallY: 5120,
  /** [confirmed: RS, RU walls[0], BW] */
  floorZ: 0,
  /** Collision plane used by both simulators.
   *  [conflict: 2048 (RS `ARENA_HEIGHT`, RU walls[1]) vs 2044 (BW, RG). No
   *   source explains the 4 uu gap. We take the simulators' 2048; see
   *   `ceilingZRLBot`.] */
  ceilingZ: 2048,
  /** The community/RLBot figure, for code that must agree with RLBot bots.
   *  [confirmed: BW, RG `CEILING_Z`] */
  ceilingZRLBot: 2044,
  /** 45-degree corner planes: |x| + |y| = this, unit normals (+-sqrt(1/2),
   *  +-sqrt(1/2), 0).
   *  [confirmed: BW, RG `FIELD_BOUNDING_BOX_SIZE`, and verified EXACTLY against
   *   the RU corner mesh — every flat-corner vertex satisfies x - y = 8064.000]
   *  [conflict: RU's own analytic walls[6..9] reference points sum to 8063.76,
   *   i.e. 0.24 uu inside the true plane. The mesh is right; RU's planes are a
   *   rounding artefact and are flagged non-collidable anyway.] */
  cornerPlaneIntercept: 8064,
  /** How much each corner cuts off along each axis: 4096 - 2944 = 5120 - 3968.
   *  [1-src: RG `CORNER_CATHETUS_LENGTH`; self-consistent with 8064.] */
  cornerCathetusLength: 1152,
} as const;

/**
 * Wall lengths as published. IMPORTANT: these are plane-intersection to
 * plane-intersection with the corner curvature IGNORED — they are NOT
 * tangent-to-tangent runs. BW says so on the line right after its table.
 * A flat wall drawn at these lengths overlaps the corner fillets by ~355 uu at
 * each end; the true tangent-to-tangent flats are ~7226.6 (side) and ~5176.9
 * (back). [1-src: BW; correction applied from the verification pass.]
 */
export const WALL_LENGTHS = {
  sideWallNominal: 7936,
  backWallNominal: 5888,
  /** 1152 * sqrt(2). [1-src: BW "Corner wall length"] */
  cornerWallNominal: 1629.174,
} as const;

/**
 * Seam radii where walls meet the floor and ceiling. All measured by parsing
 * RLUtilities' soccar collision meshes byte-level and fitting circles; the fits
 * were independently reproduced in the verification pass.
 *
 * [conflict/caveat: BW warns these ramps "are not circular". The side-wall fit
 *  residual is tiny (0.047 uu) so 256 is safe, but the CORNER VERTICAL fillet
 *  is measurably non-circular — its sample-by-sample radius of curvature runs
 *  907 -> 865 — so ~862 is an effective radius, not a true one. Only the
 *  side-wall 256 has any published corroboration (RG `RAMP_HEIGHT`, and BW as a
 *  radius); the 160 / 512 / 862 values are mesh-measurement-only.]
 */
export const WALL_FILLETS = {
  /** Side wall <-> floor. Quarter-cylinder, axis along Y, centre (|x| = 3840,
   *  z = 256). [confirmed: mesh fit r = 256.19, plus BW "Aprox. 256"] */
  sideWallToFloor: 256,
  /** Back wall <-> floor. DIFFERENT from the side wall. Centre (|y| = 4960,
   *  z = 160). [1-src: mesh fit r = 160.15, max residual 0.027 uu. No published
   *  source states this; the widely-cited "ramp height 256" is the side wall
   *  only.] */
  backWallToFloor: 160,
  /** 45-degree corner plane <-> floor. Same 256 profile as the side wall: at
   *  z = 0 the corner plane has receded to |x| + |y| = 7701.96 = 8064 - 256*sqrt(2).
   *  [1-src: mesh, but the (offset, z) ladder is byte-identical to the side-wall
   *   ramp, which is strong internal corroboration.] */
  cornerToFloor: 256,
  cornerPlaneInterceptAtFloor: 7701.96,
  /** Every wall <-> ceiling seam, uniform around the arena. Quarter-cylinder
   *  centred 512 uu in from the wall and 512 uu below the ceiling.
   *  [1-src: mesh fit r = 513.06; the identical z-ladder appears in the ramps,
   *   goal and corner meshes, so it is at least uniform.] */
  wallToCeiling: 512,
  /** Vertical fillet blending the side wall / 45-degree plane / back wall.
   *  APPROXIMATE and non-circular; treat as 862 +/- 3.
   *  [1-src: two mesh fits, 861.46 (centre +-3232.56, -+3613.32) and 863.40
   *   (centre +-2588.46, -+4254.67). The verification pass showed both arcs have
   *   identical perpendicular-offset profiles, so reporting two different radii
   *   is spurious — it is one nominal fillet, mirrored.] */
  cornerVerticalApprox: 862,
} as const;

/** Endpoints of the exactly-flat 45-degree corner run, at any wall height.
 *  Both satisfy |x| + |y| = 8064 exactly.
 *  [1-src: mesh, exact vertices. Note the flat run is 640.470 * sqrt(2) =
 *   905.76 uu, NOT the 905.097 you get by rounding the endpoint to 3840 first.] */
export const CORNER_FLAT_RUN = {
  start: { x: 3840.47, y: -4223.53 },
  end: { x: 3200.0, y: -4864.0 },
  length: 905.76,
} as const;

// ---------------------------------------------------------------------------
// Goals
// ---------------------------------------------------------------------------

/**
 * Goal geometry. Mouth plane at y = +-5120; blue defends -Y (team 0).
 *
 * [conflict: THREE mutually inconsistent sets are all confirmed in primary
 *  sources — published 892.755 half-width / 642.775 height (BW, RG, and RS's own
 *  IsBallProbablyGoingIn); collision-mesh interior 896.0 / 640.0 (byte-level
 *  parse of RU's goal mesh, independently reproduced); and RU's navigation
 *  obstacle 893 / 642. We expose the published pair as the primary values
 *  because they are what every other implementation and every bot uses, and the
 *  mesh pair separately for collision work. Which one the game's real trigger
 *  uses is UNKNOWN.]
 */
export const GOAL = {
  /** Plane of the goal mouth. [confirmed: RS, RU mesh translation, RG] */
  mouthY: 5120,
  /** Published half-width (centre to post). [confirmed: BW, RG, RS] */
  centerToPost: 892.755,
  /** Published opening height. [confirmed: BW, RG, RS] */
  height: 642.775,
  /** Collision-mesh interior half-width. [1-src: mesh parse, reproduced] */
  centerToPostMesh: 896.0,
  /** Collision-mesh interior height. [1-src: mesh parse, reproduced] */
  heightMesh: 640.0,
  /** Mouth to back of net. [confirmed: BW, and RU's mesh y range -879.996] */
  depth: 880,
  /** Back of the net. [confirmed: RG `BACK_NET_Y`, RU mesh] */
  backNetY: 6000,
  /** The goal-box interior cross-section is a rectangle with corners filleted
   *  at this radius, arcs centred at (+-800, ., 544) and (+-800, ., 96).
   *  [1-src: mesh fit, 95.98-96.02, reproduced in verification] */
  cornerFilletRadius: 96,
  /** A goal is scored when |ball.y| > this + BALL_RADIUS = 5215.5.
   *  [confirmed: RS `SOCCAR_GOAL_SCORE_BASE_THRESHOLD_Y` + its IsBallScored
   *   test, RG `GOAL_THRESHOLD = 5215.5`] */
  scoreBaseThresholdY: 5124.25,
  scoreThresholdY: 5215.5,
} as const;

/** Goal centres, using the published height. Blue (team 0) defends -Y.
 *  [confirmed: RG `BLUE_GOAL_CENTER` / `ORANGE_GOAL_CENTER`] */
export const GOAL_CENTER: readonly [Vec3, Vec3] = [
  { x: 0, y: -GOAL.mouthY, z: GOAL.height / 2 },
  { x: 0, y: GOAL.mouthY, z: GOAL.height / 2 },
];

// ---------------------------------------------------------------------------
// Ball spawn
// ---------------------------------------------------------------------------

/** Kickoff ball state: centre field, at rest height, zero linear and angular
 *  velocity. [confirmed: RS `BallState()` default pos.z = `BALL_REST_Z` used by
 *   ResetToRandomKickoff; RG `BALL_RESTING_HEIGHT`; BW "elevation at rest"] */
export const BALL_SPAWN: Vec3 = { x: 0, y: 0, z: 93.15 };

// ---------------------------------------------------------------------------
// Kickoff and respawn spawns
// ---------------------------------------------------------------------------

export interface Spawn {
  /** Spawn position. z is CAR_SPAWN_REST_Z (kickoffs) or CAR_RESPAWN_Z (demos). */
  position: Vec3;
  /** Yaw in radians, in our frame. */
  yaw: number;
  /** Label from the RLBot wiki's spawn table. */
  name: string;
}

const PI_4 = Math.PI / 4;

/**
 * The 5 standard blue (team 0) kickoff spawns, in RocketSim's array order.
 * Cars spawn on the ground with BOOST_SPAWN_AMOUNT = 33.33 boost.
 * [confirmed: RS `CAR_SPAWN_LOCATIONS_SOCCAR` (yaw stored as M_PI_4 * 1, 3, 2,
 *  2, 2) and `CAR_SPAWN_REST_Z`; BW's kickoff table is identical including the
 *  position names]
 */
export const KICKOFF_SPAWNS_BLUE: readonly Spawn[] = [
  { position: { x: -2048, y: -2560, z: 17 }, yaw: PI_4 * 1, name: 'right corner' },
  { position: { x: 2048, y: -2560, z: 17 }, yaw: PI_4 * 3, name: 'left corner' },
  { position: { x: -256, y: -3840, z: 17 }, yaw: PI_4 * 2, name: 'back right' },
  { position: { x: 256, y: -3840, z: 17 }, yaw: PI_4 * 2, name: 'back left' },
  { position: { x: 0, y: -4608, z: 17 }, yaw: PI_4 * 2, name: 'far back center' },
];

/**
 * Orange (team 1) kickoff spawns. Derived by RocketSim's exact mirroring rule
 * `pos *= (-1, -1, 1); yaw += PI`, then normalized into (-PI, PI].
 * [confirmed: RS ResetToRandomKickoff; BW's table lists exactly these values]
 */
export const KICKOFF_SPAWNS_ORANGE: readonly Spawn[] = KICKOFF_SPAWNS_BLUE.map((s) => ({
  position: { x: -s.position.x, y: -s.position.y, z: s.position.z },
  // Every blue yaw is in (0, PI], so `+ PI` always wraps to `- PI`. Gives
  // -0.75*PI, -0.25*PI, -0.5*PI, -0.5*PI, -0.5*PI, matching BW's orange table.
  yaw: s.yaw - Math.PI,
  name: s.name,
}));

/** Indexed by team: 0 = blue (defends -Y), 1 = orange. */
export const KICKOFF_SPAWNS: readonly (readonly Spawn[])[] = [
  KICKOFF_SPAWNS_BLUE,
  KICKOFF_SPAWNS_ORANGE,
];

/** The 4 blue demolition-respawn locations, at CAR_RESPAWN_Z = 36.
 *  [confirmed: RS `CAR_RESPAWN_LOCATIONS_SOCCAR` (all at M_PI/2) and
 *   `CAR_RESPAWN_Z`; BW's "Demolished Spawn Locations" table matches, labels
 *   included] */
export const RESPAWN_SPAWNS_BLUE: readonly Spawn[] = [
  { position: { x: -2304, y: -4608, z: 36 }, yaw: Math.PI / 2, name: 'right inside' },
  { position: { x: -2688, y: -4608, z: 36 }, yaw: Math.PI / 2, name: 'right outside' },
  { position: { x: 2304, y: -4608, z: 36 }, yaw: Math.PI / 2, name: 'left inside' },
  { position: { x: 2688, y: -4608, z: 36 }, yaw: Math.PI / 2, name: 'left outside' },
];

/** Orange respawns, mirrored by the same rule as the kickoffs. */
export const RESPAWN_SPAWNS_ORANGE: readonly Spawn[] = RESPAWN_SPAWNS_BLUE.map((s) => ({
  position: { x: -s.position.x, y: -s.position.y, z: s.position.z },
  yaw: -Math.PI / 2,
  name: s.name,
}));

export const RESPAWN_SPAWNS: readonly (readonly Spawn[])[] = [
  RESPAWN_SPAWNS_BLUE,
  RESPAWN_SPAWNS_ORANGE,
];

// ---------------------------------------------------------------------------
// Boost pads
// ---------------------------------------------------------------------------

export interface BoostPad {
  position: Vec3;
  /** Big pads give 100 boost and respawn in 10 s; small pads 12 boost / 4 s.
   *  See BOOST_PAD_* in ./constants. */
  isBig: boolean;
}

/**
 * All 34 soccar boost pads, in canonical RLBot FieldInfo index order (0-33).
 * 28 small at z = 70, 6 big at z = 73.
 *
 * [confirmed: RS `LOCS_SMALL_SOCCAR` + `LOCS_BIG_SOCCAR` and BW's 34-entry
 *  FieldInfo table, cross-checked entry for entry]
 * [conflict at index 27 ONLY: RS and BW both say (-940, 3308, 70); RG says
 *  (-940, 3310, 70). RG's own mirror pad (index 5) is at -3308, so RG is
 *  internally asymmetric — almost certainly a typo. We take the 2-source value
 *  3308. Every other pad agrees across all three sources.]
 */
export const BOOST_PADS: readonly BoostPad[] = [
  { position: { x: 0, y: -4240, z: 70 }, isBig: false }, // 0
  { position: { x: -1792, y: -4184, z: 70 }, isBig: false }, // 1
  { position: { x: 1792, y: -4184, z: 70 }, isBig: false }, // 2
  { position: { x: -3072, y: -4096, z: 73 }, isBig: true }, // 3
  { position: { x: 3072, y: -4096, z: 73 }, isBig: true }, // 4
  { position: { x: -940, y: -3308, z: 70 }, isBig: false }, // 5
  { position: { x: 940, y: -3308, z: 70 }, isBig: false }, // 6
  { position: { x: 0, y: -2816, z: 70 }, isBig: false }, // 7
  { position: { x: -3584, y: -2484, z: 70 }, isBig: false }, // 8
  { position: { x: 3584, y: -2484, z: 70 }, isBig: false }, // 9
  { position: { x: -1788, y: -2300, z: 70 }, isBig: false }, // 10
  { position: { x: 1788, y: -2300, z: 70 }, isBig: false }, // 11
  { position: { x: -2048, y: -1036, z: 70 }, isBig: false }, // 12
  { position: { x: 0, y: -1024, z: 70 }, isBig: false }, // 13
  { position: { x: 2048, y: -1036, z: 70 }, isBig: false }, // 14
  { position: { x: -3584, y: 0, z: 73 }, isBig: true }, // 15
  { position: { x: -1024, y: 0, z: 70 }, isBig: false }, // 16
  { position: { x: 1024, y: 0, z: 70 }, isBig: false }, // 17
  { position: { x: 3584, y: 0, z: 73 }, isBig: true }, // 18
  { position: { x: -2048, y: 1036, z: 70 }, isBig: false }, // 19
  { position: { x: 0, y: 1024, z: 70 }, isBig: false }, // 20
  { position: { x: 2048, y: 1036, z: 70 }, isBig: false }, // 21
  { position: { x: -1788, y: 2300, z: 70 }, isBig: false }, // 22
  { position: { x: 1788, y: 2300, z: 70 }, isBig: false }, // 23
  { position: { x: -3584, y: 2484, z: 70 }, isBig: false }, // 24
  { position: { x: 3584, y: 2484, z: 70 }, isBig: false }, // 25
  { position: { x: 0, y: 2816, z: 70 }, isBig: false }, // 26
  // 27: RS + BW say 3308; RLGym says 3310 and breaks its own mirror. See above.
  { position: { x: -940, y: 3308, z: 70 }, isBig: false }, // 27
  { position: { x: 940, y: 3308, z: 70 }, isBig: false }, // 28
  { position: { x: -3072, y: 4096, z: 73 }, isBig: true }, // 29
  { position: { x: 3072, y: 4096, z: 73 }, isBig: true }, // 30
  { position: { x: -1792, y: 4184, z: 70 }, isBig: false }, // 31
  { position: { x: 1792, y: 4184, z: 70 }, isBig: false }, // 32
  { position: { x: 0, y: 4240, z: 70 }, isBig: false }, // 33
];

/** 6 big, 28 small. [confirmed: RS `LOCS_AMOUNT_BIG` / `LOCS_AMOUNT_SMALL_SOCCAR`,
 *  BW] */
export const BIG_PAD_COUNT = 6;
export const SMALL_PAD_COUNT = 28;
